import { combineEpics, Epic, ActionsObservable, StateObservable, ofType } from 'redux-observable';
import { RootAction, RootState } from '@src/app/_redux';
import { ApiClient } from '@src/app/_global/utility/apiUtils';
import { ApiConfig } from '@src/app/_global/domain';
import { map, mergeMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';
import { concat } from 'rxjs/observable/concat';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { actionCreators, ORDER_UPDATE_START, RESIN_ORDER_UPDATE_START } from '@src/app/accounts/orders/actions';
import { actionCreators as gaActionCreators } from '@src/app/analytics/googleAnalytics/actions';
import { actionCreators as weakActionCreators } from '@src/app/login/weak/actions';
import { actionCreators as intelliselActionCreators } from '@src/app/intellisel/actions';
import { actionCreators as custServActionCreators } from '@src/app/accounts/customer-service/actions';
import {
  config as AWSConfig,
  CognitoIdentityCredentials,
} from 'aws-sdk/global';
import { OrderUpdatePutbody, ResinOrderUpdatePutbody } from '@src/_api/payload/orderupdate';
import { forkJoin } from 'rxjs';

var AWS = require('aws-sdk');

const ORDER_UPDATE_PATH = '/orderupdate';
const RESIN_ORDER_UPDATE_PATH = '/resinorderupdate'; 

const getAllSucceeded = (...responses) => {
  const successCount = responses.filter(item => item.success);
  return successCount.length === responses.length;
};

const orderUpdate = (
  action$: ActionsObservable<RootAction>,
  state$: StateObservable<RootState>,
) =>
  action$.pipe(
    ofType(ORDER_UPDATE_START),
    mergeMap(({ payload }) => {
      const clientConfig = state$.value.clientConfig.clientConfig;
      AWS.config.region = clientConfig.Region;
      const loading = of(actionCreators.orderUpdateLoading(true));
      if (!AWS.config.credentials) {
        const creds = state$.value.auth.omsAuth.credentials;
        AWS.config.credentials = new AWS.CognitoIdentityCredentials(creds);
      }
      const client = new ApiClient();
      const promises = payload.map(item => {
        let body: OrderUpdatePutbody = {
          prodIdAlias: item.prodIdAlias,
          accountNumber: item.accountNumber,
          order: {
            orderNumber: item.orderNumber,
            invoiceNumber: item.invoiceNumber,
            orderStatus: item.orderStatus,
            reasonCode: item.reasonCode,
            giftSet: item.giftSet,
            orderItems: item.orderItems,
            cancel: item.orderStatus === 'cancel' ? 'Y' : '',
            migration: item.migration,
            toMagAbbr: item.toMagAbbr,
            toDocKey: item.toDocKey,
            moveSw: item.moveSw,
            toService: item.toService,
            newTerm: item.newTerm,
            newValue: item.newValue,
            changeOffer: item.changeOffer,
            refundType: item.refundType,
          },
        };
        const apiConfig: ApiConfig = {
          creds: AWSConfig.credentials as CognitoIdentityCredentials,
          url: clientConfig.url,
          path: ORDER_UPDATE_PATH,
          region: clientConfig.Region,
          requestmethod: 'PUT',
          requestbody: body,
          parms: {},
          additionalParms: {
            headers: {
              'x-session-id': clientConfig.uuid
            },
          },
        };
        return fromPromise(client.callApiGateway(apiConfig));
      });

      const source$ = forkJoin(promises, getAllSucceeded).pipe(
        map(allSucceeded => {
          let gaCategory = payload[0].googleAnalyticsCategory === 'Subscription Transfer'
            ? payload[0].googleAnalyticsCategory + ' to ' + payload[0].toMagAbbr : payload[0].googleAnalyticsCategory;
          if (allSucceeded) {
            gaActionCreators.createGAEvent({
              category: gaCategory,
              action: 'Successful',
              label: payload[0].prodIdAlias
            });
            return actionCreators.orderUpdateSuccess({accountResponse: payload, success: allSucceeded});
          } else {
            gaActionCreators.createGAEvent({
              category:  gaCategory,
              action: 'Unsuccessful',
              label: payload[0].prodIdAlias
            });
            if (payload[0].refundType && payload[0].refundType === 'R') {
              return custServActionCreators.sendCustservEmailStart(payload[0].custServEmailData);
            } else {
              return actionCreators.orderUpdateError({
                success: false,
                message: 'Error on ' + gaCategory,
              });
            }           
          }
        }),
        catchError(err => {
          gaActionCreators.createGAEvent({
            category:  payload[0].googleAnalyticsCategory,
            action: 'Unknown Error',
            label: payload[0].prodIdAlias
          });
          if (payload[0].refundType && payload[0].refundType === 'R') {
            return of(custServActionCreators.sendCustservEmailStart(payload[0].custServEmailData));
          } else {
            return of(
              actionCreators.orderUpdateError({ success: false, message: err }),
            );
          }        
        }),
      );
      const refetchCustomerPayload = {
        accountNumber: payload.accountNumber,
        postalCode: payload.postalCode,
      };
      let reloadCust = of(weakActionCreators.refetchCustomer(refetchCustomerPayload));
      if (payload[0].intelliselPayload) {
        let intelliselAcceptOffer = of(intelliselActionCreators.intelliselStart(payload[0].intelliselPayload));
        return concat(loading, source$, intelliselAcceptOffer, reloadCust);
      } else {
        return concat(loading, source$, reloadCust);
      }
    }),
  );

const resinOrderUpdate = (
  action$: ActionsObservable<RootAction>,
  state$: StateObservable<RootState>,
) =>
  action$.pipe(
    ofType(RESIN_ORDER_UPDATE_START),
    mergeMap(({ payload }) => {
      const clientConfig = state$.value.clientConfig.clientConfig;
      AWS.config.region = clientConfig.Region;
      const loading = of(actionCreators.resinOrderUpdateLoading(true));
      if (!AWS.config.credentials) {
        const creds = state$.value.auth.omsAuth.credentials;
        AWS.config.credentials = new AWS.CognitoIdentityCredentials(creds);
      }
      const client = new ApiClient();
   
      let body: ResinOrderUpdatePutbody = {
        resinClientId: payload.resinClientId,
        systemCode: payload.systemCode,
        resinGiftRedemptionId: payload.resinGiftRedemptionId,
        resinStoreCredit: payload.resinStoreCredit,
        path: payload.path,
        reasonCode: payload.reasonCode,
        resinRecipientEmail: payload.resinRecipientEmail
      };
      const apiConfig: ApiConfig = {
        creds: AWSConfig.credentials as CognitoIdentityCredentials,
        url: clientConfig.url,
        path: RESIN_ORDER_UPDATE_PATH,
        region: clientConfig.Region,
        requestmethod: 'PUT',
        requestbody: body,
        parms: {},
        additionalParms: {
          headers: {
            'x-session-id': clientConfig.uuid
          },
        },
      };

      const source$ = fromPromise(client.callApiGateway(apiConfig)).pipe(
        map(answer => {
          const isSuccess = String(answer.success) === 'true';
          if (isSuccess) {
            if (payload.path === 'cancellation') {
              gaActionCreators.createGAEvent({
                category: payload.googleAnalyticsCategory,
                action: 'Successful',
                label: payload.prodIdAlias,
                value: parseFloat(payload.refundAmount)  * 100,
              });
            } else {
              gaActionCreators.createGAEvent({
                category: payload.googleAnalyticsCategory,
                action: 'Successful',
                label: payload.prodIdAlias,
              });
            }
            return actionCreators.resinOrderUpdateSuccess({accountResponse: answer, success: isSuccess});
          } else {
            if (payload.path === 'cancellation') {
              gaActionCreators.createGAEvent({
                category: payload.googleAnalyticsCategory,
                action: 'Unsuccessful',
                label: payload.prodIdAlias,
                value: parseFloat(payload.refundAmount)  * 100,
              });
            } else {
              gaActionCreators.createGAEvent({
                category: payload.googleAnalyticsCategory,
                action: 'Unsuccessful',
                label: payload.prodIdAlias,
              });
            }
            return actionCreators.resinOrderUpdateError({
              success: false,
              message: 'Error on ' + payload.googleAnalyticsCategory,
            });    
          }
        }),
        catchError(err => {
          gaActionCreators.createGAEvent({
            category:  payload.googleAnalyticsCategory,
            action: 'Unknown Error',
            label: payload.prodIdAlias
          });
          return of(
            actionCreators.resinOrderUpdateError({ success: false, message: err }),
          );         
        }),
      );
      const refetchCustomerPayload = {
        accountNumber: payload.accountNumber,
        postalCode: payload.postalCode,
      };
      let reloadCust = of(weakActionCreators.refetchCustomer(refetchCustomerPayload));
      return concat(loading, source$, reloadCust);
    }),
  );

export const epics = combineEpics(orderUpdate, resinOrderUpdate);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/accounts/orders/epics.ts